import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Send, FileText, Trash2, Users, Plus, Eye } from "lucide-react";

function StatusBadge({ status }) {
  const s = status === "sent" ? "Sent" : "Draft";
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
        status === "sent"
          ? "border-[#C05A46]/40 text-[#C05A46] bg-[#C05A46]/8"
          : "border-border text-[#786B63] bg-[#F9F6F0]"
      }`}
    >
      {s}
    </span>
  );
}

export default function AdminNewslettersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState(null); // newsletter being edited
  const [form, setForm] = useState({ subject: "", preheader: "", html_content: "" });
  const [saving, setSaving] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    if (user === false) {
      navigate("/login", { state: { from: "/admin/newsletters" } });
    } else if (user && user.role !== "admin") {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: n }] = await Promise.all([
        api.get("/admin/subscribers"),
        api.get("/admin/newsletters"),
      ]);
      setStats({ total: s.total, active: s.active });
      setNewsletters(n);
    } catch (e) {
      // 403 likely; auth gate above handles redirect
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  const openNew = () => {
    setEditing(null);
    setForm({ subject: "", preheader: "", html_content: "" });
    setShowCompose(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      subject: n.subject,
      preheader: n.preheader || "",
      html_content: n.html_content,
    });
    setShowCompose(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/newsletters/${editing.id}`, form);
        toast("Draft updated.");
      } else {
        await api.post("/admin/newsletters", form);
        toast("Draft saved.");
      }
      setShowCompose(false);
      await load();
    } catch (e) {
      toast("Couldn't save", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async (n) => {
    if (!window.confirm(`Mark "${n.subject}" as sent? This will record ${stats.active} recipients.`)) return;
    try {
      const { data } = await api.post(`/admin/newsletters/${n.id}/send`);
      toast(`Marked as sent (${data.recipient_count} subscribers)`, {
        description: data.delivered ? "Delivered." : "Note: email delivery not configured — record-only.",
      });
      await load();
    } catch (e) {
      toast("Couldn't send", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete draft "${n.subject}"?`)) return;
    try {
      await api.delete(`/admin/newsletters/${n.id}`);
      toast("Deleted.");
      await load();
    } catch (e) {
      toast("Couldn't delete", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const sorted = useMemo(
    () => [...newsletters].sort((a, b) => (a.status === "draft" ? -1 : 1) - (b.status === "draft" ? -1 : 1)),
    [newsletters]
  );

  if (user && user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="admin-newsletters-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Editor desk</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">The Dispatch</h1>
          <p className="text-[#786B63] mt-3 max-w-xl">
            Compose, save drafts, and send weekly notes to the BrewFinder community.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0] h-11"
          data-testid="new-newsletter-btn"
        >
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New newsletter
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-10 grid sm:grid-cols-3 gap-6">
        <div className="border border-border bg-card rounded-sm p-6">
          <Users className="h-5 w-5 text-[#C05A46]" strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63] mt-4">Active subscribers</p>
          <p className="font-serif text-5xl mt-1" data-testid="stat-active">{stats.active}</p>
        </div>
        <div className="border border-border bg-card rounded-sm p-6">
          <Mail className="h-5 w-5 text-[#C05A46]" strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63] mt-4">Total signups (all-time)</p>
          <p className="font-serif text-5xl mt-1" data-testid="stat-total">{stats.total}</p>
        </div>
        <div className="border border-border bg-card rounded-sm p-6">
          <FileText className="h-5 w-5 text-[#C05A46]" strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63] mt-4">Drafts & sent</p>
          <p className="font-serif text-5xl mt-1" data-testid="stat-newsletters">{newsletters.length}</p>
        </div>
      </div>

      {/* Newsletters list */}
      <h2 className="font-serif text-3xl mt-14">All newsletters</h2>
      <div className="mt-6 space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="border border-border bg-card rounded-sm h-24 animate-pulse" />
          ))
        ) : sorted.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-10 text-center">
            <p className="font-serif text-2xl">No newsletters yet.</p>
            <p className="text-sm text-[#786B63] mt-2">Click "New newsletter" to compose your first dispatch.</p>
          </div>
        ) : sorted.map((n) => (
          <div
            key={n.id}
            data-testid={`newsletter-row-${n.id}`}
            className="border border-border bg-card rounded-sm p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center"
          >
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-serif text-2xl">{n.subject}</h3>
                <StatusBadge status={n.status} />
              </div>
              {n.preheader && (
                <p className="text-sm text-[#786B63] mt-1 line-clamp-1">{n.preheader}</p>
              )}
              <p className="text-xs text-[#786B63] mt-2">
                {n.status === "sent"
                  ? `Sent ${new Date(n.sent_at).toLocaleDateString()} · ${n.recipient_count} recipients`
                  : `Last edited ${new Date(n.updated_at).toLocaleDateString()}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => setPreviewItem(n)}
                data-testid={`preview-${n.id}`}
              >
                <Eye className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Preview
              </Button>
              {n.status === "draft" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => openEdit(n)}
                    data-testid={`edit-${n.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
                    onClick={() => sendNow(n)}
                    data-testid={`send-${n.id}`}
                  >
                    <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Send
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm text-[#C05A46]"
                    onClick={() => remove(n)}
                    data-testid={`delete-${n.id}`}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Compose Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-3xl bg-[#F9F6F0] border-border rounded-sm" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              {editing ? "Edit draft" : "New newsletter"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="This week in specialty coffee"
                className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11"
                data-testid="compose-subject"
              />
            </div>
            <div>
              <Label htmlFor="preheader">Preheader (preview text)</Label>
              <Input
                id="preheader"
                value={form.preheader}
                onChange={(e) => setForm({ ...form, preheader: e.target.value })}
                placeholder="3 new spots, 1 review worth reading, 1 bean shop worth a detour."
                className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11"
                data-testid="compose-preheader"
              />
            </div>
            <div>
              <Label htmlFor="html">Content (HTML allowed) *</Label>
              <Textarea
                id="html"
                rows={10}
                value={form.html_content}
                onChange={(e) => setForm({ ...form, html_content: e.target.value })}
                placeholder="<h2>Hello, fellow drinker…</h2><p>This week we discovered…</p>"
                className="mt-1.5 rounded-sm bg-[#F2EBE1] font-mono text-xs"
                data-testid="compose-html"
              />
              <p className="text-xs text-[#786B63] mt-1">Tip: keep it under 50KB. Inline CSS only — most email clients ignore stylesheets.</p>
            </div>
            {form.html_content && (
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Live preview</p>
                <div
                  className="mt-2 border border-border rounded-sm p-5 bg-white text-[#2C1E16] max-h-72 overflow-auto"
                  data-testid="compose-preview"
                  dangerouslySetInnerHTML={{ __html: form.html_content }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCompose(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !form.subject || !form.html_content}
              className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]"
              data-testid="compose-save"
            >
              {saving ? "Saving…" : editing ? "Update draft" : "Save draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-3xl bg-[#F9F6F0] border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{previewItem?.subject}</DialogTitle>
            {previewItem?.preheader && (
              <p className="text-sm text-[#786B63]">{previewItem.preheader}</p>
            )}
          </DialogHeader>
          <div
            className="border border-border rounded-sm p-5 bg-white text-[#2C1E16] max-h-[60vh] overflow-auto"
            dangerouslySetInnerHTML={{ __html: previewItem?.html_content || "" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
